import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2, ChevronUp, ChevronDown, X, AlertTriangle } from "lucide-react";
import {
  useListItemModifiers,
  useCreateItemModifier,
  useDeleteItemModifier,
  useListInventoryItems,
  getListItemModifiersQueryKey,
} from "@workspace/api-client-react";
import type { InventoryRecipeModifier } from "@workspace/api-client-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { specsToLine } from "@/lib/specs";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

const ALLOWED_GST_RATES = ["0", "5", "12", "18", "28"] as const;

interface ModifierListSectionProps {
  itemId: number;
  itemName: string;
}

// Phase 56 Plan 56-04 — form shape extended with logicType + components[] (a
// sub-list of picker rows). Replaces the legacy singular componentItemId +
// qtyPerUse fields. Plain useState — matches the existing 511-line component's
// style (no react-hook-form migration).
interface ComponentRowState {
  componentItemId: number | null;
  qtyPerUse: string;
  sortOrder: number;
}

interface AddModifierForm {
  modifierName: string;
  addPrice: string;
  gstRate: string;
  logicType: "AND" | "OR";
  components: ComponentRowState[];
  status: "Active" | "Archived";
}

const DEFAULT_FORM: AddModifierForm = {
  modifierName: "",
  addPrice: "0",
  gstRate: "0",
  logicType: "AND",
  components: [],
  status: "Active",
};

// Minimal shape of an inventory item as consumed by the ComponentRow picker.
// Kept as a structural type so the row component works with either the CRM
// item-list result or a caller-supplied list.
type PickerItem = {
  id: number;
  name: string;
  brand?: string | null;
  specs?: Array<{ label: string; value: string }> | null;
  unitOfMeasurement?: string | null;
  inventoryTracked?: boolean | null;
  currentStock?: string | number | null;
};

export function ModifierListSection({ itemId, itemName }: ModifierListSectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InventoryRecipeModifier | null>(null);
  const [form, setForm] = useState<AddModifierForm>(DEFAULT_FORM);
  const [isPending, setIsPending] = useState(false);
  // Track whether the operator has flipped AND↔OR with existing components so
  // we can surface the semantic-change warning (Open Q3 LOCKED — allow the
  // switch, warn about intent). Cleared on form reset.
  const [logicTouched, setLogicTouched] = useState(false);

  const { data: modifiers = [], isLoading } = useListItemModifiers(itemId, undefined, {
    query: {
      queryKey: getListItemModifiersQueryKey(itemId),
      enabled: true,
    },
  });

  const { data: allItems = [] } = useListInventoryItems({ status: "Active" });

  const createMutation = useCreateItemModifier();
  const deleteMutation = useDeleteItemModifier();

  // Filter list for the picker (excludes the parent item + any recipe items —
  // per Phase 55 nested-recipe guard). Search filter is per-row now.
  const eligibleItems = useMemo<PickerItem[]>(
    () =>
      (allItems as unknown as PickerItem[]).filter(
        (it) => it.id !== itemId && !(it as { hasRecipe?: boolean }).hasRecipe,
      ),
    [allItems, itemId],
  );

  function resetForm() {
    setForm(DEFAULT_FORM);
    setLogicTouched(false);
  }

  function reorderComponent(from: number, to: number) {
    setForm((f) => {
      const next = [...f.components];
      const [moved] = next.splice(from, 1);
      if (moved) next.splice(to, 0, moved);
      // Re-number sortOrder to match array position (server sorts by sortOrder
      // ASC on read — keeps editor state and server state aligned).
      return { ...f, components: next.map((c, i) => ({ ...c, sortOrder: i })) };
    });
  }

  function updateComponent(idx: number, patch: Partial<ComponentRowState>) {
    setForm((f) => ({
      ...f,
      components: f.components.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  }

  function removeComponent(idx: number) {
    setForm((f) => ({
      ...f,
      components: f.components
        .filter((_, i) => i !== idx)
        .map((c, i) => ({ ...c, sortOrder: i })),
    }));
  }

  function addComponent() {
    setForm((f) => ({
      ...f,
      components: [
        ...f.components,
        { componentItemId: null, qtyPerUse: "1", sortOrder: f.components.length },
      ],
    }));
  }

  function setLogicType(next: "AND" | "OR") {
    setForm((f) => {
      if (f.components.length > 0 && f.logicType !== next) setLogicTouched(true);
      return { ...f, logicType: next };
    });
  }

  // Client-side validity — mirrors the server-side Zod refinements from Plan
  // 56-02 so the operator gets the reject inline instead of a 422 toast.
  const validationError = useMemo<string | null>(() => {
    if (!form.modifierName.trim()) return "Modifier name is required.";
    if (form.addPrice === "" || isNaN(Number(form.addPrice)) || Number(form.addPrice) < 0) {
      return "Add price must be 0 or greater.";
    }
    if (!ALLOWED_GST_RATES.includes(form.gstRate as typeof ALLOWED_GST_RATES[number])) {
      return "Select a valid GST rate.";
    }
    if (form.logicType === "OR" && form.components.length === 1) {
      return "OR requires at least 2 components — add another or switch to AND.";
    }
    for (const [i, c] of form.components.entries()) {
      if (c.componentItemId == null) return `Pick a component for row ${i + 1}.`;
      if (!c.qtyPerUse || isNaN(Number(c.qtyPerUse)) || Number(c.qtyPerUse) <= 0) {
        return `Qty per use must be > 0 on row ${i + 1}.`;
      }
    }
    const ids = form.components.map((c) => c.componentItemId).filter((id): id is number => id != null);
    if (new Set(ids).size !== ids.length) return "Same component listed twice — components must be unique.";
    return null;
  }, [form]);

  async function handleAddModifier() {
    if (validationError) {
      toast({ title: validationError, variant: "destructive" });
      return;
    }

    setIsPending(true);
    try {
      await createMutation.mutateAsync({
        itemId,
        // Phase 56 Plan 56-04 — send the new components[] + logicType shape.
        // Do NOT send legacy top-level componentItemId + qtyPerUse; the
        // server's ambiguity guard 422s if BOTH are present. Annotation-only
        // modifiers send components: [] (server accepts + treats as AND with 0
        // linked components — Open Q5 LOCKED).
        data: {
          modifierName: form.modifierName.trim(),
          addPrice: form.addPrice,
          gstRate: form.gstRate,
          logicType: form.logicType,
          components: form.components.map((c) => ({
            componentItemId: c.componentItemId as number,
            qtyPerUse: Number(c.qtyPerUse),
            sortOrder: c.sortOrder,
          })),
          status: form.status,
        },
      });
      queryClient.invalidateQueries({ queryKey: getListItemModifiersQueryKey(itemId) });
      toast({ title: "Modifier added." });
      setAddOpen(false);
      resetForm();
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      const data = (err as { data?: { code?: string; error?: string; componentName?: string } })?.data;
      if (status === 422) {
        if (data?.code === "nested_recipe_not_allowed" && data?.componentName) {
          toast({
            title: `${data.componentName} is a recipe item — modifiers cannot link to recipes. Remove it and pick a raw ingredient.`,
            variant: "destructive",
          });
        } else {
          toast({
            title: data?.error ?? "Validation error: check components, qty_per_use, and GST rate.",
            variant: "destructive",
          });
        }
      } else {
        toast({ title: "Failed to add modifier. Please try again.", variant: "destructive" });
      }
    } finally {
      setIsPending(false);
    }
  }

  async function handleDeleteModifier() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ itemId, modifierId: deleteTarget.id });
      queryClient.invalidateQueries({ queryKey: getListItemModifiersQueryKey(itemId) });
      toast({ title: "Modifier removed." });
    } catch {
      toast({ title: "Failed to remove modifier.", variant: "destructive" });
    } finally {
      setDeleteTarget(null);
    }
  }

  function getItemName(id: number) {
    return allItems.find((it) => it.id === id)?.name ?? `Item #${id}`;
  }

  // Table-body cell: render a modifier's components as a joined list —
  // ' + ' for AND (all decrement together), ' / ' for OR (operator picks
  // one). Falls back to the legacy single-component column for pre-Phase-56
  // rows the server may still return during rollout.
  function renderComponentsCell(m: InventoryRecipeModifier): React.ReactNode {
    const components = m.components ?? [];
    if (components.length === 0) {
      if (m.componentItemId != null) {
        return getItemName(m.componentItemId);
      }
      return <span className="text-muted-foreground italic">annotation only</span>;
    }
    const sep = m.logicType === "OR" ? " / " : " + ";
    return components.map((c) => getItemName(c.componentItemId)).join(sep);
  }

  function renderQtyCell(m: InventoryRecipeModifier): React.ReactNode {
    const components = m.components ?? [];
    if (components.length === 0) {
      return m.qtyPerUse != null ? Number(m.qtyPerUse).toFixed(3) : "—";
    }
    if (components.length === 1) {
      return Number(components[0]!.qtyPerUse).toFixed(3);
    }
    // Multi-component — render an aggregate hint; per-link qty is visible in
    // the editor (visual UAT in Plan 56-05 will confirm this is legible).
    return <span className="text-xs text-muted-foreground">(multi)</span>;
  }

  return (
    <>
      <Separator className="my-4" />
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">Modifiers (POS add-ons)</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add-ons like &lsquo;+Cheese&rsquo; attach to &ldquo;{itemName}&rdquo; at Stock_Out time.
              Modifiers with linked components automatically decrement those components&apos; stock.
              AND = all components decrement together; OR = operator picks one at sale.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => { resetForm(); setAddOpen(true); }}
          >
            + Add Modifier
          </Button>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading modifiers…</p>
        ) : modifiers.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-md border border-dashed px-3 py-2">
            No modifiers. For recipe items, add modifiers like &lsquo;+Cheese ₹20&rsquo; so reception can apply them at Stock_Out.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Add Price</TableHead>
                <TableHead>GST</TableHead>
                <TableHead>Logic</TableHead>
                <TableHead>Components</TableHead>
                <TableHead>Qty/Use</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {modifiers.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">{m.modifierName}</TableCell>
                  <TableCell className="font-mono text-xs">₹{Number(m.addPrice).toFixed(2)}</TableCell>
                  <TableCell className="text-xs">{m.gstRate}%</TableCell>
                  <TableCell>
                    <Badge
                      variant={m.logicType === "OR" ? "outline" : "secondary"}
                      className={cn(
                        "text-[10px] tabular-nums",
                        m.logicType === "OR" && "border-amber-300 bg-amber-50 text-amber-900",
                      )}
                    >
                      {m.logicType}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{renderComponentsCell(m)}</TableCell>
                  <TableCell className="font-mono text-xs">{renderQtyCell(m)}</TableCell>
                  <TableCell>
                    <Badge
                      variant={m.status === "Active" ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(m)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Add Modifier Dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) { setAddOpen(false); resetForm(); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Modifier</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>
                Modifier Name <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="e.g. Cheese, Extra Masala, Less Salt"
                value={form.modifierName}
                onChange={(e) => setForm((f) => ({ ...f, modifierName: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Add Price ₹ <span className="text-destructive">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0"
                  value={form.addPrice}
                  onChange={(e) => setForm((f) => ({ ...f, addPrice: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>GST Rate <span className="text-destructive">*</span></Label>
                <Select
                  value={form.gstRate}
                  onValueChange={(v) => setForm((f) => ({ ...f, gstRate: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALLOWED_GST_RATES.map((r) => (
                      <SelectItem key={r} value={r}>{r}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Composition Logic</Label>
              <Select
                value={form.logicType}
                onValueChange={(v) => setLogicType(v as "AND" | "OR")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">AND — all linked components decrement together</SelectItem>
                  <SelectItem value="OR">OR — operator picks one component at sale</SelectItem>
                </SelectContent>
              </Select>
              {logicTouched && form.components.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    Switching to <b>{form.logicType}</b>:{" "}
                    {form.logicType === "AND"
                      ? "all linked components will decrement together on each use."
                      : "operator picks one component at sale time. Save to persist."}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Linked Components</Label>
                <Button type="button" size="sm" variant="outline" onClick={addComponent}>
                  + Add component
                </Button>
              </div>
              {form.components.length === 0 ? (
                <p className="text-xs text-muted-foreground rounded-md border border-dashed px-3 py-2">
                  No components — annotation-only modifier (add price + GST are still charged; no stock decrement).
                </p>
              ) : (
                <div className="space-y-2">
                  {form.components.map((row, idx) => (
                    <ComponentRow
                      key={idx}
                      row={row}
                      items={eligibleItems}
                      canMoveUp={idx > 0}
                      canMoveDown={idx < form.components.length - 1}
                      onChange={(patch) => updateComponent(idx, patch)}
                      onRemove={() => removeComponent(idx)}
                      onMoveUp={() => reorderComponent(idx, idx - 1)}
                      onMoveDown={() => reorderComponent(idx, idx + 1)}
                    />
                  ))}
                </div>
              )}
              {form.logicType === "OR" && form.components.length === 1 && (
                <p className="text-xs text-destructive">
                  OR requires at least 2 components — add another or switch to AND.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v as "Active" | "Archived" }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setAddOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddModifier}
              disabled={isPending || validationError != null}
            >
              {isPending ? "Saving…" : "Add Modifier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget != null}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove modifier?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove modifier &ldquo;{deleteTarget?.modifierName}&rdquo; from &ldquo;{itemName}&rdquo;.
              Existing stock_out records with this modifier are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteModifier}
              disabled={deleteMutation.isPending}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// --------------------------------------------------------------------------
// Inline ComponentRow — one row inside the modifier editor's components
// sub-list. Uses the same Popover-Combobox pattern as the singular picker so
// the operator visual is consistent with the recipe-component picker.
// --------------------------------------------------------------------------
interface ComponentRowProps {
  row: ComponentRowState;
  items: PickerItem[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  onChange: (patch: Partial<ComponentRowState>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function ComponentRow({
  row,
  items,
  canMoveUp,
  canMoveDown,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: ComponentRowProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredItems = useMemo(
    () =>
      items.filter((it) => it.name.toLowerCase().includes(search.toLowerCase())),
    [items, search],
  );
  const selected = items.find((it) => it.id === row.componentItemId);

  return (
    <div className="flex items-start gap-2 rounded-md border p-2">
      <div className="flex flex-col gap-0.5">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          disabled={!canMoveUp}
          onClick={onMoveUp}
          aria-label="Move up"
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          disabled={!canMoveDown}
          onClick={onMoveDown}
          aria-label="Move down"
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>

      <div className="grid flex-1 grid-cols-3 gap-2">
        <div className="col-span-2 space-y-1">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                size="sm"
                className="w-full justify-between"
              >
                <span className={cn("truncate text-left", !selected && "text-muted-foreground")}>
                  {selected ? (
                    <>
                      {selected.name}
                      {selected.brand ? ` · ${selected.brand}` : ""}
                      {specsToLine(selected.specs) ? ` · ${specsToLine(selected.specs)}` : ""}
                    </>
                  ) : (
                    "Pick component…"
                  )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search items…"
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList>
                  <CommandEmpty>No items found.</CommandEmpty>
                  <CommandGroup>
                    {filteredItems.map((it) => {
                      const specs = specsToLine(it.specs);
                      const availNum =
                        it.inventoryTracked === false
                          ? null
                          : it.currentStock != null
                            ? Number(it.currentStock)
                            : null;
                      const avail = availNum != null && Number.isFinite(availNum) ? availNum : null;
                      return (
                        <CommandItem
                          key={it.id}
                          value={String(it.id)}
                          onSelect={() => {
                            onChange({ componentItemId: it.id });
                            setSearch("");
                            setOpen(false);
                          }}
                          className="py-2"
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4 shrink-0",
                              row.componentItemId === it.id ? "opacity-100" : "opacity-0",
                            )}
                          />
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="text-sm truncate">
                              {it.name}
                              {it.brand ? ` · ${it.brand}` : ""}
                            </span>
                            {(specs || it.unitOfMeasurement) && (
                              <span className="text-[11px] text-muted-foreground truncate">
                                {[specs, it.unitOfMeasurement ? `(${it.unitOfMeasurement})` : null]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            )}
                          </div>
                          {avail != null && (
                            <span
                              className={cn(
                                "ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
                                avail <= 0
                                  ? "bg-destructive/10 text-destructive"
                                  : "bg-emerald-50 text-emerald-700",
                              )}
                            >
                              {avail} in stock
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-1">
          <Input
            type="number"
            step="0.001"
            min="0.001"
            placeholder="Qty/use"
            value={row.qtyPerUse}
            onChange={(e) => onChange({ qtyPerUse: e.target.value })}
          />
        </div>
      </div>

      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
        onClick={onRemove}
        aria-label="Remove component"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
